import * as React from 'react';
import cx from 'classnames';
import { DatasheetComponentTextStyle } from '@common/dto/datasheet/component-settings/text-style';
import { DatasheetFieldComponentTextAlign } from '@common/dto/datasheet/field-component/text-align';

import { PRINTABLE_DATASHEET_FIELD_ID } from '../datasheet-printing-settings';

import './datasheet-item.styles.scss';

export type DatasheetItemProps = React.PropsWithChildren<{
  style: React.CSSProperties;
  displayType?: "block" | "flex";
  className?: string;
  img?: string;
  text?: string;
  textCustomization?: {
    fontSize?: number;
    fontFamily?: string;
    color?: number;
    align?: DatasheetFieldComponentTextAlign;
    fontStyles?: Array<DatasheetComponentTextStyle>;
  };
  reference?: React.RefObject<HTMLDivElement>;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
}>;

export const DatasheetItem: React.FC<DatasheetItemProps> = (props) => {
  const { children, style, displayType, className, img, text, textCustomization , reference, onMouseDown } = props;

  const displayTypeClassName = displayType
    ? `DatasheetsDatasheetItem--${displayType}`
    : undefined;

  // Map text alignment enum to CSS text-align values
  const textAlignMap: Record<DatasheetFieldComponentTextAlign, React.CSSProperties['textAlign']> = {
    [DatasheetFieldComponentTextAlign.left]: 'left',
    [DatasheetFieldComponentTextAlign.center]: 'center',
    [DatasheetFieldComponentTextAlign.right]: 'right',
  };

  // Convert textCustomization to CSS properties
  const textStyle: React.CSSProperties = React.useMemo(() => {
    if (!textCustomization) return {};
    
    // Convert BGR number to RGB hex string
    let color = '#000000';
    if (textCustomization.color !== undefined && textCustomization.color !== null) {
      const colorNum = typeof textCustomization.color === 'number' ? textCustomization.color : 0;
      const blue = (colorNum & 0xFF);
      const green = ((colorNum >> 8) & 0xFF);
      const red = ((colorNum >> 16) & 0xFF);
      const hex = [
        red.toString(16).padStart(2, '0'),
        green.toString(16).padStart(2, '0'),
        blue.toString(16).padStart(2, '0')
      ].join('');
      color = `#${hex}`;
    }

    return {
      fontSize: textCustomization.fontSize,
      fontFamily: textCustomization.fontFamily,
      color: color,
      textAlign: textCustomization.align ? textAlignMap[textCustomization.align] : undefined,
      fontStyle: textCustomization.fontStyles?.includes(DatasheetComponentTextStyle.italic) ? 'italic' : undefined,
      fontWeight: textCustomization.fontStyles?.includes(DatasheetComponentTextStyle.bold) ? 'bold' : undefined,
      textDecoration: textCustomization.fontStyles?.includes(DatasheetComponentTextStyle.underline) ? 'underline' : 'none',
    };
  }, [textCustomization]);

  return (
    <div 
      ref={reference}
      style={style}
      data-printable-element={PRINTABLE_DATASHEET_FIELD_ID}
      className={cx("DatasheetsDatasheetItem", displayTypeClassName, className)}
      onMouseDown={onMouseDown}
    >
      {img && <img src={img} alt={"image"} style={{ pointerEvents: 'none', marginRight: "2px" }} />}
      {text !== undefined && text !== null && <span style={{ ...textStyle, pointerEvents: 'none' }}>{text}</span>}
      {children}
    </div>
  );
};